import { NgIf, NgFor, AsyncPipe, TitleCasePipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import {
  MatNavList,
  MatListItem,
  MatListItemAvatar,
  MatListItemTitle,
  MatListItemLine,
  MatListItemMeta,
} from '@angular/material/list';
import { MatToolbar, MatToolbarRow } from '@angular/material/toolbar';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { BrandingService } from '@next/core-lib/branding';
import { BlockSpinnerDirective, NotificationEmptyComponent } from '@next/core-lib/components';
import { TranslatePipe, AgrDatePipe, AgrNumberPipe } from '@next/core-lib/i18n';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, take, withLatestFrom } from 'rxjs/operators';

import { getBarns } from '../../../settings/store/selectors/setting.selector';
import { Barn, DayOldChick } from '../../models/cycle.model';
import * as cycleActions from '../../store/actions/cycle.actions';
import { getSelectedCycle } from '../../store/selectors/cycle.selectors';
import { getBarnsInUse, getDayOldChicks } from '../../store/selectors/day-old-chick.selectors';

@Component({
  selector: 'app-day-old-chick',
  templateUrl: 'day-old-chick.component.html',
  styleUrls: ['./day-old-chick.component.scss'],
  imports: [
    MatToolbar,
    MatToolbarRow,
    NgIf,
    MatButton,
    MatIcon,
    BlockSpinnerDirective,
    MatNavList,
    NgFor,
    MatListItem,
    RouterLink,
    MatListItemAvatar,
    MatListItemTitle,
    MatListItemLine,
    MatIconButton,
    MatListItemMeta,
    MatDivider,
    NotificationEmptyComponent,
    AsyncPipe,
    TitleCasePipe,
    TranslatePipe,
    AgrDatePipe,
    AgrNumberPipe,
  ],
})
export class DayOldChickComponent {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  readonly brandingService = inject(BrandingService);

  barns$: Observable<Barn[]> = this.store.select(getBarns);
  hasBarns$: Observable<boolean> = this.barns$.pipe(
    withLatestFrom(this.store.select(getBarnsInUse)),
    map(
      ([barns, barnsInUse]) =>
        (!!barns && barns.length > 0) || (barnsInUse && barnsInUse.length > 0),
    ),
  );
  dayOldChicks$: Observable<DayOldChick[]> = this.store.select(getDayOldChicks);
  totalAmountOfDayOldChicks$: Observable<number>;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    this.totalAmountOfDayOldChicks$ = this.dayOldChicks$.pipe(
      filter((dayOldChicks) => !!dayOldChicks),
      map((dayOldChicks) =>
        dayOldChicks.reduce(
          (totalAmountOfChicks, dayOldChick) => totalAmountOfChicks + dayOldChick.amountOfChicks,
          0,
        ),
      ),
    );
  }

  add = () => this.router.navigate(['add'], { relativeTo: this.activatedRoute });

  delete(event: Event, id: number) {
    event.preventDefault();
    event.stopImmediatePropagation();

    this.store
      .pipe(select(getSelectedCycle), withLatestFrom(this.dayOldChicks$), take(1))
      .subscribe(([cycle, dayOldChicks]) => {
        let isUsedInBalance = false;
        if (cycle.balancesPerDeliveryLine.length > 0) {
          const thisDayoldChicksBarn = dayOldChicks.find((doc) => doc.id === id).barn;
          isUsedInBalance =
            cycle.balancesPerDeliveryLine.findIndex((line) =>
              line.barnBalances.find(
                (barnbalance) => barnbalance.barnId === thisDayoldChicksBarn.id,
              ),
            ) !== -1;
        }

        this.store.dispatch(
          cycleActions.deleteDayOldChickWithConfirmation({
            cycleId: cycle.id,
            id,
            isUsedInBalance,
          }),
        );
      });
  }
}
